<template>
  <v-container>
    <v-stepper v-model="currentStep">
      <v-stepper-items>
        <!-- Paso 1: Identificación del Paciente (solo RUT) -->
        <v-stepper-content step="1">
          <v-card class="pa-4">
            <h2 class="headline mb-4 text-center">¡Bienvenido! Estamos aquí para ayudarte</h2>
            <p class="body-1 text-center mb-6 grey--text">Por favor, identifícate: ingresa el RUT del paciente</p>

            <v-alert v-if="sessionExpired" type="info" dense outlined class="mb-4">
              Tu sesión de reserva expiró. Ingresa tu RUT nuevamente para continuar.
            </v-alert>

            <v-form v-model="validPatientForm" ref="patientForm" @submit.prevent="identifyPatient">
              <div class="pa-2">
                <v-text-field
                  v-model="rut"
                  label="Rut"
                  required
                  outlined
                  validate-on-blur
                  :rules="[
                    () => !!rut || 'Campo requerido',
                    () => isValidRut || 'Rut inválido'
                  ]"
                ></v-text-field>
              </div>
            </v-form>

            <v-btn
              color="primary"
              @click="identifyPatient"
              :disabled="!isValidRut"
              :loading="identifying"
              large
              block
            >
              Continuar
            </v-btn>

            <!-- Separador con opción de autenticación -->
            <div class="d-flex align-center my-6">
              <v-divider></v-divider>
              <span class="px-3 grey--text">O puedes autenticarte</span>
              <v-divider></v-divider>
            </div>

            <div class="text-center">
              <v-btn @click="goToLogin" outlined large>
                <v-icon left>mdi-account</v-icon>
                Iniciar Sesión
              </v-btn>
            </div>
          </v-card>
        </v-stepper-content>

        <!-- Paso 2: Completar datos faltantes (fecha nacimiento / género / previsión) -->
        <v-stepper-content step="2">
          <div v-if="showChangeRut" class="d-flex justify-end mb-2">
            <v-btn text small color="primary" @click="resetIdentification">
              <v-icon left small>mdi-account-switch</v-icon>Ingresar otro RUT
            </v-btn>
          </div>
          <complete-profile-step
            v-if="currentStep == 2"
            :is-new-patient="isNewPatient"
            :rut="rut"
            :requires-birth-date="bookingFlags && bookingFlags.requiresBirthDate"
            :requires-agreement="bookingFlags && bookingFlags.requiresAgreement"
            @completed="onProfileCompleted"
          ></complete-profile-step>
        </v-stepper-content>

        <!-- Paso 3: Búsqueda de profesionales y horas -->
        <v-stepper-content step="3">
    <div v-if="showChangeRut" class="d-flex align-center justify-space-between mb-4">
      <span class="body-2 grey--text">
        <v-icon small class="mr-1">mdi-account-check</v-icon>
        <template v-if="identifiedRut">Buscando horas para RUT {{ identifiedRut }}</template>
        <template v-else>Paciente identificado</template>
      </span>
      <v-btn text small color="primary" @click="resetIdentification">
        <v-icon left small>mdi-account-switch</v-icon>Ingresar otro RUT
      </v-btn>
    </div>
    <v-card class="mb-6 pa-4">
      <p class="body-2">
        <v-icon>filter_list</v-icon>
        <span class="ml-4">Filtro de profesionales</span>
      </p>
      <v-autocomplete
        class="mb-6"
        v-model="selectedSpecialty"
        :items="specialtyList"
        item-value="id"
        label="Especialidad"
        clearable
        hide-details
      >
        <template v-slot:selection="data">{{ data.item.name }}</template>
        <template v-slot:item="data">
          <v-list-item-content>
            <v-list-item-title v-html="data.item.name"></v-list-item-title>
          </v-list-item-content>
        </template>
      </v-autocomplete>


      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="true"
        :return-value.sync="date"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            v-model="fromDate"
            label="Desde"
            prepend-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            v-on="on"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="fromDate"
          no-title
          scrollable
          :min="new Date().toISOString().substr(0, 10)"
        >

        </v-date-picker>
      </v-menu>

      <br />

      <p class="body-2">
        <v-icon>people</v-icon>
        <span class="ml-4">Selección de profesionales</span>
      </p>

      <div
        class="d-flex"
        style="overflow-x: auto; flex-wrap: initial; padding-bottom: 6px;"
        v-if="professionalList.length"
      >
        <v-btn
          v-for="professional of professionalList"
          @click="professional.selected = !professional.selected"
          v-bind:key="professional.id"
          class="mr-2"
          v-bind:class="{ 'primary': professional.selected }"
          style=" min-width: 130px;
                    max-width:130px;
                    height:110px;"
        >
          <div class="d-flex flex-column align-center">
            <professional-avatar v-bind:id-professional="professional.id" v-bind:size="60"></professional-avatar>
            <span
              class="caption"
              style="
                width: 120px;
                text-overflow: ellipsis;
                overflow: hidden;
                margin-top: 6px;"
            >{{professional.name}} {{professional.fLastName}}</span>
            <span class="overline font-weight-light">{{professional.specialty.name}}</span>
          </div>
        </v-btn>
      </div>
      <div
        v-else
        style="height:110px;"
        class="d-flex justify-center align-center font-weight-light"
      >
        <p class="text-center">No hay profesionales disponibles</p>
      </div>
    </v-card>
    <v-divider></v-divider>
    <div v-if="this.selectedProfessional && this.selectedProfessional.length">
      <v-subheader>
        <span class="flex text-center">Resultados</span>
      </v-subheader>
      <div v-if="loading">
        <p class="text-center font-weight-light">Cargando...</p>
      </div>
      <div v-else>
        <div v-for="(bookingListItem, date, index) in bookingByDate" v-bind:key="date">
          <v-subheader>
            <span class="primary--text">{{date}}</span>
          </v-subheader>
          <v-expansion-panels>
            <v-expansion-panel
              v-for="booking in bookingListItem"
              class="animated fadeIn"
              v-bind:key="booking.id"
            >
              <v-expansion-panel-header>
                <div class="d-flex">
                  <professional-avatar
                    v-bind:id-professional="booking.professional.id"
                    v-bind:size="50"
                  ></professional-avatar>
                  <div class="flex flex-column">
                    <p
                      class="text-center body-1 mb-0"
                    >{{booking.professional.name}} {{booking.professional.fLastName}}</p>
                    <p class="text-center mb-0 caption">{{booking.hourComputed}}</p>
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pa-0">
                <v-row style="max-width: 500px;margin:auto;">
                  <v-col
                    cols="3"
                    sm="2"
                    class="pa-0"
                    align-self="center"
                    v-for="hour in booking.hourList"
                    :key="hour.time"
                  >
                    <v-chip
                      @click="showTakeHourDialog(hour, booking)"
                      class="mb-2 mr-2"
                      label
                      outlined
                      color="primary"
                    >{{ hour.start }}</v-chip>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
        <p
          v-if="bookingList && bookingList.length == 0"
          class="text-center font-weight-light"
        >No hay citas disponibles</p>
      </div>
    </div>
    <div v-else>
      <p
        class="text-center mt-4 font-weight-light"
      >Selecciona profesionales para ver citas disponibles</p>
    </div>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <v-snackbar v-model="showError" :timeout="6000" color="error" top>
      {{ errorMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import CompleteProfileStep from "./CompleteProfileStep.vue";
import { isValidRut as rutIsValid, formatRut, toApiFormat } from "../utils/rut";

export default {
  name: "SearchHour",
  components: { CompleteProfileStep },
  props: {
    msg: String
  },
  data: function() {
    return {
      // Identificación (FEAT-2026-07-08-001): solo RUT, el backend resuelve
      // la previsión real del paciente y fuerza el filtrado de horas.
      currentStep: 1,
      rut: "",
      isValidRut: false,
      isNewPatient: false,
      validPatientForm: false,
      identifying: false,
      sessionExpired: false,
      showError: false,
      errorMessage: "",

      // Existente
      hourSelected: null,
      selectedCompany: null,
      selectedSpecialty: null,
      fromDate: null,
      selectedProfessional: null,
      rutValidationTimeout: null,
      selectedDate: new Date(),
      menu: false,  // Para el v-menu del date picker
      date: null,   // Para el return-value del date picker
      companyList: [],
      specialtyList: [],
      professionalList: [],
      hourList: [],
      bookingList: null,
      takeHourConfirm: false,
      takeHourSuccessMessage: false,
      expandSingle: true,
      loading: null
    };
  },
  methods: {
    async identifyPatient() {
      if (!this.isValidRut) return;
      this.identifying = true;
      try {
        let result = await this.$store.dispatch("identify", toApiFormat(this.rut));

        if (!result.isRegistered) {
          // Paciente NUEVO → seguir inline (transparente, sin ir a /registrarse):
          // el formulario por secciones pide sus datos + contacto + previsión.
          this.isNewPatient = true;
          this.currentStep = 2;
          return;
        }

        // Paciente existente → siempre mostramos el formulario (contacto se
        // pide siempre; y datos faltantes si los hubiera).
        this.isNewPatient = false;
        this.currentStep = 2;
      } catch (err) {
        this.errorMessage =
          err.response && err.response.status === 429
            ? "Demasiados intentos. Espera unos minutos e intenta nuevamente."
            : "No fue posible procesar la solicitud. Intenta nuevamente.";
        this.showError = true;
      } finally {
        this.identifying = false;
      }
    },

    // Decide el paso según los datos que falten (flags del backend)
    goToNextStep() {
      let flags = this.bookingFlags || {};
      if (flags.requiresBirthDate || flags.requiresAgreement) {
        this.currentStep = 2;
      } else {
        this.currentStep = 3;
        this.getProfessionalList();
      }
    },

    onProfileCompleted() {
      this.currentStep = 3;
      this.getProfessionalList();
    },

    // Volver a identificar con otro RUT (por si el usuario se equivocó).
    // Limpia la sesión efímera de reserva y reinicia el flujo al paso 1.
    resetIdentification() {
      this.$store.dispatch("clearBookingSession");
      this.rut = "";
      this.isValidRut = false;
      this.sessionExpired = false;
      this.selectedSpecialty = null;
      this.professionalList = [];
      this.bookingList = null;
      this.selectedProfessional = null;
      this.currentStep = 1;
      this.$nextTick(() => {
        if (this.$refs.patientForm) this.$refs.patientForm.resetValidation();
      });
    },

    // Un 428 significa que el backend exige completar datos antes de buscar
    handle428(err) {
      if (err.response && err.response.status === 428) {
        let code = (err.response.data || {}).code;
        this.$store.dispatch("setBookingFlags", {
          requiresBirthDate: code === "BIRTHDATE_REQUIRED",
          requiresAgreement: code === "AGREEMENT_REQUIRED"
        });
        this.currentStep = 2;
        return true;
      }
      return false;
    },

    goToLogin() {
      // Guardar ruta actual para volver después del login
      this.$store.dispatch('saveFromUrl', this.$route.fullPath);
      this.$router.push('/ingreso');
    },

    showTakeHourDialog: function(hour, booking) {
      // Solo el RUT identificado: el backend resuelve el resto desde el
      // token de reserva (no viajan datos personales por el cliente)
      this.$store.dispatch('setTemporaryPatient', {
        rut: this.rut ? formatRut(this.rut) : (this.$store.getters.getUser || {}).rut
      });

      this.$router.push({
        path: `/reservar-cita/${hour.id}`,
        query: { hasPatientData: true }
      });
    },
    getProfessionalList: function() {
      const axios = require("axios");
      axios
        .get("getProfessionalList", {
          params: {
            idSpecialty: this.selectedSpecialty
          }
        })
        .then(
          res => {
            this.professionalList = res.data.map(professional =>
              Object.assign(professional, { selected: false })
            );
          },
          err => {
            if (!this.handle428(err)) console.log(err);
          }
        );
    },
    getData: function() {
      clearTimeout(this.loading);
      this.loading = setTimeout(() => {
        const axios = require("axios");
        axios
          .get("searchHour", {
            params: {
              from:this.fromDate,
              idSpecialty: this.selectedSpecialty,
              idProfessional: this.professionalList
                .filter(({ selected }) => selected)
                .map(({ id }) => id)
            }
          })
          .then(
            res => {
              this.loading = false;
              this.bookingList = res.data.map(booking =>
                Object.assign(booking, {
                  hourComputed: booking.hourList.reduce(
                    (computed, currentHour, index, arr) => {
                      let suspensivos = index == arr.length - 1 ? "... " : "";
                      if (index < 3 || suspensivos) {
                        return (
                          computed +
                          suspensivos +
                          "(" +
                          currentHour.start +
                          ") "
                        );
                      }
                      return computed;
                    },
                    ""
                  )
                })
              );
            },
            err => {
              this.loading = false;
              if (!this.handle428(err)) console.log(err);
            }
          );
      }, 900);
    }
  },
  computed: {
    bookingFlags() {
      return this.$store.getters.getBookingFlags;
    },
    // Solo el flujo público (identificado por RUT) puede "cambiar de RUT".
    // El paciente logueado se identifica por su sesión, no aplica.
    showChangeRut() {
      return this.$store.getters.hasBookingSession && !this.$store.getters.isLoggedIn;
    },
    identifiedRut() {
      return this.rut ? formatRut(this.rut) : "";
    },
    bookingByDate() {
      if (!this.bookingList) {
        return [];
      }
      return this.bookingList.reduce((acc, booking) => {
        (acc[booking.date] = acc[booking.date] || []).push(booking);
        return acc;
      }, {});
    }
  },
  watch: {
    rut() {
      clearTimeout(this.rutValidationTimeout);
      this.rutValidationTimeout = setTimeout(() => {
        this.isValidRut = rutIsValid(this.rut);
        if (this.isValidRut) {
          this.rut = formatRut(this.rut);
        }
      }, 600);
    },
    selectedSpecialty: function() {
      this.getProfessionalList();
    },
    fromDate:function() {
       if (this.selectedProfessional.length) {
          this.getData();
        }
    },
    professionalList: {
      handler: function(val, oldVal) {
        this.bookingList = null;
        this.selectedProfessional = this.professionalList
          .filter(({ selected }) => selected)
          .map(({ id }) => id);
        if (this.selectedProfessional.length) {
          this.getData();
        }
      },
      deep: true
    },
    $route(to, from) {
      if (from.name == "Buscar cita") {
        this.selectedSpecialty = null;
        this.bookingList = null;

        this.getProfessionalList();
      }
    }
  },
  mounted: function() {
    const axios = require("axios");
    axios.get("getSpecialtyList").then(
      res => {
        this.specialtyList = res.data;
      },
      err => console.log(err)
    );

    this.sessionExpired = this.$route.query.expired === "1";

    if (this.$store.getters.isLoggedIn) {
      // Usuario autenticado: su sesión identifica al paciente. El contacto se
      // pide siempre → paso 2 (el formulario solo mostrará lo que falte + contacto).
      this.isNewPatient = false;
      this.currentStep = 2;
    } else if (this.$store.getters.hasBookingSession) {
      // Ya se identificó en esta pestaña (token efímero vigente)
      this.isNewPatient = false;
      this.currentStep = 2;
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>
